import { useEffect } from "react";
import { useUploadProgressState } from "@/zustand/UploadProgressState";

/**
 * Bridges the service worker's chunked-stream prefetch progress into
 * `UploadProgressState`, so a receiver's attachment bubble shows the same
 * progress ring the sender gets while uploading.
 *
 * The SW posts:
 *  - `PREFETCH_PROGRESS { uploadId, storedChunks, totalChunks }` each time a
 *    chunk of an in-progress upload lands in its OPFS cache;
 *  - `PREFETCH_DONE { uploadId }` when the transfer settles (upload completed
 *    → served from the assembled file; or failed/cancelled).
 *
 * Mount once at the chat-page level — progress must keep updating while the
 * preview dialog is closed (the SW prefetch loop runs in the background after
 * the first preview attempt).
 */
export function usePrefetchProgressBridge(): void {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as
        | {
            type?: string;
            uploadId?: string;
            storedChunks?: number;
            totalChunks?: number;
          }
        | undefined;
      if (!data?.type || typeof data.uploadId !== "string") {
        return;
      }

      if (data.type === "PREFETCH_DONE") {
        useUploadProgressState.getState().clearProgress(data.uploadId);
        return;
      }

      if (data.type !== "PREFETCH_PROGRESS") {
        return;
      }
      const { storedChunks, totalChunks } = data;
      if (
        typeof storedChunks !== "number" ||
        typeof totalChunks !== "number" ||
        totalChunks <= 0
      ) {
        return;
      }
      const pct = Math.floor((storedChunks / totalChunks) * 100);
      if (pct >= 100) {
        // Everything is local — the ring's job is done.
        useUploadProgressState.getState().clearProgress(data.uploadId);
      } else {
        useUploadProgressState.getState().setProgress(data.uploadId, pct);
      }
    };

    // Capture the container so cleanup doesn't depend on the global still
    // being present at unmount time.
    const sw = navigator.serviceWorker;
    sw.addEventListener("message", handleMessage);
    return () => {
      sw.removeEventListener("message", handleMessage);
    };
  }, []);
}
